import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  totalOrders: number;
  totalSpent: number;
  createdAt?: string;
}

interface Subscription {
  id: string;
  status: string;
  nextOrderDate: string;
  frequency: string;
  platformOrderId: string;
  createdAt: string;
  price?: number;
  cancelledAt?: string;
}

interface Order {
  id: string;
  orderNumber: string;
  createdAt: string;
  totalPrice: number;
  financialStatus: string;
  fulfillmentStatus: string;
}

interface AffiliateStats {
  referralCode: string;
  totalSales: number;
  totalEarnings: number;
  pendingEarnings: number;
}

interface DashboardData {
  customer: Customer;
  subscriptions: Subscription[];
  orders: Order[];
  affiliateStats: AffiliateStats | null;
}

interface AuthState {
  token: string | null;
  merchantId: string | null;
  customer: Customer | null;
  dashboardData: DashboardData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  setToken: (token: string, merchantId: string) => void;
  setCustomer: (customer: Customer) => void;
  setDashboardData: (data: DashboardData) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      merchantId: null,
      customer: null,
      dashboardData: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      setToken: (token: string, merchantId: string) =>
        set({ token, merchantId, isAuthenticated: true }),

      setCustomer: (customer: Customer) =>
        set({ customer }),

      setDashboardData: (data: DashboardData) =>
        set({ dashboardData: data, customer: data.customer }),

      setLoading: (loading: boolean) =>
        set({ isLoading: loading }),

      setError: (error: string | null) =>
        set({ error }),

      logout: () =>
        set({
          token: null,
          merchantId: null,
          customer: null,
          dashboardData: null,
          isAuthenticated: false,
          error: null
        }),
    }),
    {
      name: 'customer-auth-storage',
    }
  )
);
